import { apiClient } from "./api-client"
import type { ReportResponse } from "@/types/report"
import type { Parameter, ReportParameterRequest, RawApiParameter } from "@/types/parameter"
import { transformParameters } from "@/lib/parameter-transformer"
import { demoReports } from "@/data/demo-data"

const BASE_PATH = "/ReportingInterface"
const isDemoMode = import.meta.env.VITE_DEMO_MODE === "true"

/**
 * Parameter names that should never be sent as query parameters to the PDF endpoint.
 * Must stay in sync with the Blazor app's exclusion list in Report.cs GetParameterQueryString().
 *
 * - Authorization:    JWT token already sent via HTTP header by axios interceptor.
 *                     Including it as a query param bloats the URL and can cause 404/431 errors.
 * - ImageBaseUrl:     Infrastructure value injected server-side by the backend from
 *                     ReportingHelperService:BaseUrl config. Sending it from the client
 *                     could overwrite the backend-injected value and break report images.
 * - ReportName:       Metadata parameter, not used by the report rendering engine.
 * - ReportVersion:    Metadata parameter, not used by the report rendering engine.
 * - ColumnNumber:     Internal layout parameter.
 * - TrimmableEndNames: Internal data parameter.
 */
export const EXCLUDED_PDF_PARAMS = new Set([
  "Authorization",
  "ImageBaseUrl",
  "ReportName",
  "ReportVersion",
  "ColumnNumber",
  "TrimmableEndNames",
])

export const reportApi = {
  async getAllReports(): Promise<ReportResponse[]> {
    if (isDemoMode) {
      return demoReports
    }
    return apiClient.get(`${BASE_PATH}/GetAllReports`)
  },

  async getReportParameters(
    reportPath: string,
    parameters?: Record<string, string | string[]>
  ): Promise<Parameter[]> {
    if (isDemoMode) {
      // Return empty parameters in demo mode
      return []
    }
    const payload: ReportParameterRequest = {
      reportName: reportPath,
      parameters: parameters || {},
    }
    const raw = await apiClient.post<RawApiParameter[]>(
      `${BASE_PATH}/GetReportParameters`,
      payload,
    )
    return transformParameters(raw)
  },

  async generatePDF(
    reportClassName: string,
    parameters: Record<string, string>
  ): Promise<Blob> {
    if (isDemoMode) {
      // Return empty PDF blob in demo mode
      return new Blob([], { type: "application/pdf" })
    }

    const params = new URLSearchParams()
    params.append("reportClassName", reportClassName)

    for (const [key, value] of Object.entries(parameters)) {
      // Skip sensitive/infrastructure parameters that shouldn't be in the URL
      if (EXCLUDED_PDF_PARAMS.has(key)) continue

      // If value contains commas, it may be a multi-select - send as repeated params
      if (value.includes(",")) {
        for (const v of value.split(",")) {
          params.append(key, v.trim())
        }
      } else {
        params.append(key, value)
      }
    }

    return apiClient.downloadFile(`${BASE_PATH}/GetReportPdfByName?${params}`)
  },
}
